import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Calendar } from '@/components/shadcn/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Label } from '@/components/shadcn/label'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { BaseFieldProps, JsonFormPropertyV2 } from './types'
import { useCallback, useId, useMemo } from 'react'
import { format, parse, isValid } from 'date-fns'
import { cn } from '@/lib/utils'
import clsx from 'clsx'
import { DocumentationButton } from './DocumentationButton'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconCalendarOutline } from '@icons/IconCalendarOutline'
import { IconCalendarClockOutline } from '@icons/IconCalendarClockOutline'
import { IconTimeOutline } from '@icons/IconTimeOutline'
import { IconCirclePlusOutline } from '@icons/IconCirclePlusOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { getDatePickerCalendarProps } from '@/lib/datePickerCalendarProps'

type ObjectEntry = Record<string, unknown>

export const ObjectListField: React.FC<BaseFieldProps> = ({ field, formFieldName, control, variant }) => {
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => <ObjectListFieldInner field={field} formField={formField} variant={variant} />}
    />
  )
}

const ObjectListFieldInner: React.FC<{
  field: BaseFieldProps['field']
  formField: { value: unknown; onChange: (v: unknown) => void; onBlur: () => void }
  variant: BaseFieldProps['variant']
}> = ({ field, formField, variant }) => {
  const subProperties = useMemo(() => field.items?.properties ?? [], [field.items?.properties])
  const entries = useMemo(() => (formField.value as ObjectEntry[]) || [], [formField.value])

  const createEmptyEntry = useCallback((): ObjectEntry => {
    const entry: ObjectEntry = {}
    for (const prop of subProperties) {
      switch (prop.type) {
        case 'boolean':
          entry[prop.id] = false
          break
        case 'integer':
        case 'number':
          entry[prop.id] = ''
          break
        default:
          entry[prop.id] = ''
      }
    }
    return entry
  }, [subProperties])

  const addEntry = useCallback(() => {
    formField.onChange([...entries, createEmptyEntry()])
  }, [entries, formField, createEmptyEntry])

  const removeEntry = useCallback(
    (index: number) => {
      const newEntries = [...entries]
      newEntries.splice(index, 1)
      formField.onChange(newEntries)
    },
    [entries, formField]
  )

  const updateEntry = useCallback(
    (index: number, propId: string, value: unknown) => {
      const newEntries = entries.map((entry, i) => (i === index ? { ...entry, [propId]: value } : entry))
      formField.onChange(newEntries)
    },
    [entries, formField]
  )

  const fieldControl = (
    <FormControl>
      <div className="flex flex-col gap-0.5">
        {/* Table header + rows */}
        {subProperties.length > 0 && (
          // <div className="w-full overflow-x-auto rounded-md border border-input">
          // <div className="dark:bg-offwhite w-full overflow-hidden rounded-lg border border-input bg-background shadow-sm">
          <div className="w-full overflow-hidden border border-input bg-background shadow-sm dark:bg-offwhite">
            <Table className="w-full table-fixed text-sm">
              <TableHeader className="[&_tr]:border-0">
                <TableRow className="!border-b border-border/80 bg-muted/60 hover:bg-muted/60">
                  {subProperties.map((prop) => (
                    <TableHead
                      key={prop.id}
                      className="h-8 px-2 py-1.5 text-center text-xs font-bold text-foreground/60"
                    >
                      {prop.title}
                    </TableHead>
                  ))}
                  {!field.readOnly && <TableHead className="h-8 w-8 px-2 py-1.5" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={subProperties.length + (field.readOnly ? 0 : 1)}
                      className="px-2 py-3 text-center text-xs font-light text-muted-foreground"
                    >
                      No entries yet. Click &quot;Add&quot; to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry, rowIndex) => (
                    <TableRow key={rowIndex} className="border-0 odd:bg-background even:bg-muted/40">
                      {subProperties.map((prop) => (
                        <TableCell key={prop.id} className="min-w-0 px-1 py-1 text-center align-middle">
                          <InlineCellInput
                            property={prop}
                            value={entry[prop.id]}
                            onChange={(value) => updateEntry(rowIndex, prop.id, value)}
                            disabled={field.readOnly}
                          />
                        </TableCell>
                      ))}
                      {!field.readOnly && (
                        <TableCell className="w-10 px-2 py-1 text-center align-middle">
                          <button
                            type="button"
                            className="rounded-full p-1 outline-none ring-offset-offwhite hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-1"
                            onClick={() => removeEntry(rowIndex)}
                          >
                            <IconCloseOutline className="size-3.5 text-muted-foreground" />
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {!field.readOnly && (
          <div className="flex w-full justify-end">
            <Button type="button" variant="ghost" size="sm" className="h-6" onClick={addEntry}>
              <span className="mr-1 text-xs font-light">...add new row</span>
              <IconCirclePlusOutline className="size-3.5" />
            </Button>
          </div>
        )}
      </div>
    </FormControl>
  )

  const isCompact = variant === 'row' || variant === 'inline'

  return (
    <FormItem
      className={clsx('flex flex-1 flex-col', {
        hidden: field.widget === 'hidden',
        'space-y-0': variant === 'inline',
      })}
    >
      <div className={clsx('flex justify-start gap-2', { hidden: variant === 'inline' })}>
        <FormLabel className={clsx({ 'sr-only': variant === 'row' })}>{field.title}</FormLabel>
        {!isCompact && <DocumentationButton documentation={field.documentation} />}
      </div>
      <FormDescription
        className={clsx({
          'sr-only': variant === 'row',
          hidden: variant === 'inline',
          'text-xs': !isCompact,
        })}
      >
        {field.description}
      </FormDescription>
      {fieldControl}
      {!isCompact && <FormMessage />}
    </FormItem>
  )
}

/** Parses a value into a Date, handling multiple common date string formats. */
const toDateValue = (value: unknown): Date | undefined => {
  if (!value) return undefined
  if (value instanceof Date) return isValid(value) ? value : undefined
  if (typeof value === 'string') {
    const input = value.trim()
    const native = new Date(input)
    if (!Number.isNaN(native.getTime())) return native
    const patterns = ['yyyy-MM-dd', 'dd-MM-yyyy', 'yyyy/MM/dd', 'dd/MM/yyyy']
    for (const p of patterns) {
      const d = parse(input, p, new Date())
      if (isValid(d)) return d
    }
    const normalized = input.replace(/\//g, '-')
    for (const p of ['yyyy-MM-dd', 'dd-MM-yyyy']) {
      const d = parse(normalized, p, new Date())
      if (isValid(d)) return d
    }
  }
  return undefined
}

/** Renders a compact inline input for a single cell within an object row. */
const InlineCellInput: React.FC<{
  property: JsonFormPropertyV2
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
}> = ({ property, value, onChange, disabled }) => {
  const timeInputId = useId()
  // String with choices → Select dropdown
  if (property.type === 'string') {
    if (property.choices && property.choices.length > 0) {
      const options = property.choices.map((choice) => {
        const rawValue = typeof choice === 'string' || typeof choice === 'number' ? choice : choice.value
        const label = typeof choice === 'string' || typeof choice === 'number' ? String(choice) : choice.title
        return {
          value: String(rawValue),
          label,
        }
      })
      return (
        <SingleSelectCombobox
          options={options}
          selectedValue={value === '' || value === undefined || value === null ? null : String(value)}
          onSelectionChange={(selectedValue) => onChange(selectedValue ?? '')}
          placeholder="Select..."
          searchPlaceholder="Search..."
          emptyMessage="No options found."
          disabled={disabled}
          triggerVariant="input"
          className="h-7 min-w-0 max-w-full overflow-hidden px-2 text-xs"
          renderSelectedOption={(option) => (
            <span className="min-w-0 flex-1 truncate text-left" title={option.label}>
              {option.label}
            </span>
          )}
        />
      )
    }
    if (property.format === 'date') {
      const dateValue = toDateValue(value)
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="input"
              className={cn(
                'h-7 w-full justify-start px-2 text-left text-xs font-normal',
                !dateValue && 'text-muted-foreground'
              )}
              disabled={disabled}
            >
              {dateValue ? format(dateValue, 'PP') : <span>Pick a date</span>}
              <IconCalendarOutline className="ml-auto size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateValue}
              onSelect={(date) => {
                if (date) {
                  onChange(format(date, 'yyyy-MM-dd'))
                }
              }}
              initialFocus
              {...getDatePickerCalendarProps(dateValue)}
            />
          </PopoverContent>
        </Popover>
      )
    }
    if (property.widget === 'textarea') {
      const previewText = String(value ?? '')
        .replace(/\s+/g, ' ')
        .trim()
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="input"
              className={cn(
                'h-7 w-full justify-between gap-2 overflow-hidden px-2 text-left text-xs font-normal',
                !previewText && 'text-muted-foreground'
              )}
              disabled={disabled}
            >
              <span className="min-w-0 truncate">{previewText || 'Enter text...'}</span>
              <span className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                <IconEditOutline className="size-3" />
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <Textarea
              value={String(value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="min-h-[120px] text-xs"
            />
          </PopoverContent>
        </Popover>
      )
    }
    if (['datetime', 'date-time'].includes(property.format ?? '')) {
      const dateTimeValue = value ? new Date(value as string) : undefined
      const validDateTime = dateTimeValue && !Number.isNaN(dateTimeValue.getTime()) ? dateTimeValue : undefined
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="input"
              className={cn(
                'h-7 w-full justify-start px-2 text-left text-xs font-normal',
                !validDateTime && 'text-muted-foreground'
              )}
              disabled={disabled}
            >
              {validDateTime ? format(validDateTime, 'PPpp') : <span>Select date & time</span>}
              <IconCalendarClockOutline className="ml-auto size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="sm:flex sm:flex-col">
              <Calendar
                mode="single"
                className="p-2"
                selected={validDateTime}
                onSelect={(date) => {
                  if (date) {
                    if (validDateTime) {
                      date.setHours(validDateTime.getHours())
                      date.setMinutes(validDateTime.getMinutes())
                      date.setSeconds(validDateTime.getSeconds())
                    }
                    onChange(format(date, "yyyy-MM-dd'T'HH:mm:ss"))
                  }
                }}
                {...getDatePickerCalendarProps(validDateTime)}
              />
              <div className="border-t p-3">
                <div className="flex items-center gap-3">
                  <Label htmlFor={timeInputId} className="text-xs">
                    Time
                  </Label>
                  <div className="relative grow">
                    <Input
                      id={timeInputId}
                      type="time"
                      step="1"
                      value={validDateTime ? format(validDateTime, 'HH:mm:ss') : ''}
                      onChange={(e) => {
                        const currentDate = validDateTime ? new Date(validDateTime) : new Date()
                        const parts = e.target.value.split(':')
                        currentDate.setHours(parseInt(parts[0]))
                        currentDate.setMinutes(parseInt(parts[1]))
                        currentDate.setSeconds(parseInt(parts[2] || '0'))
                        onChange(format(currentDate, "yyyy-MM-dd'T'HH:mm:ss"))
                      }}
                      className="peer appearance-none ps-9 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    />
                    <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
                      <IconTimeOutline size={16} aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )
    }
  }

  // Boolean → Checkbox
  if (property.type === 'boolean') {
    return (
      <div className="flex h-7 items-center justify-center">
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
          disabled={disabled}
        />
      </div>
    )
  }

  // Number / Integer → number input
  if (property.type === 'number' || property.type === 'integer') {
    return (
      <Input
        type="number"
        value={value === '' || value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const num = e.target.valueAsNumber
          onChange(Number.isNaN(num) ? '' : num)
        }}
        disabled={disabled}
        className="h-7 border-0 bg-transparent px-2 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-ring/50"
      />
    )
  }

  // Default: string input
  return (
    <Input
      type="text"
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-7 border-0 bg-transparent px-2 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-ring/50"
    />
  )
}
